import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Helmet } from "react-helmet";

import { useTheme } from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import FormControlLabel from "@mui/material/FormControlLabel";
import Grid from "@mui/material/Grid";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Snackbar from "@mui/material/Snackbar";
import TextField from "@mui/material/TextField";
import Link from "@mui/material/Link";

import LoginIcon from "@mui/icons-material/Login";

import { requestSilent } from "@/Api";
import { GOOGLE_CLIENT_ID, APP_VERSION } from "@/config";
import { GlobalStyle } from "@/themes";
import {
    AuthContainer,
    AuthVideo,
    AuthSection,
    Heading,
    SubHeading,
    GoogleLoginButton,
    OrLine,
    Footer,
} from "@/auth/StyledComponents";
import { InternalLink, PageTitle } from "@/common/CommonComponents";
import { setToken } from "@/auth/authSlice";
import useInvitation from "@/auth//authHooks";
import AuthNav from "@/auth/AuthNav";
import Analytics from "@/Analytics";

export default function SignUp({ toggleTheme }) {
    const theme = useTheme();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { inviteToken } = useParams();

    const [loading, setLoading] = useState(false);
    const [step, setStep] = useState(1);
    const [email, setEmail] = useState("");
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [companyName, setCompanyName] = useState("");
    const [password, setPassword] = useState("");
    const [password2, setPassword2] = useState("");
    const [agree, setAgree] = useState(false);
    const [errors, setErrors] = useState([]);
    const [invitationLoaded, setInvitationLoaded] = useState(false);

    const themeMode = "dark";
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));

    const invitation = useInvitation();

    useEffect(() => {
        if (invitation && !invitationLoaded) {
            invitation.tryGoToLogin();
            setEmail(invitation.invitee.email);
            setFirstName(invitation.invitee.first_name);
            setLastName(invitation.invitee.last_name);
            setInvitationLoaded(true);
        }
    }, [invitation]);

    const signUp = async (data) => {
        setErrors([]);
        setLoading(true);

        if (APP_VERSION) {
            data.last_seen_release_version = APP_VERSION;
        }

        requestSilent
            .post("sign-up", { ...data, invitation_token: inviteToken })
            .then((response) => {
                if (response.data) {
                    dispatch(setToken(response.data));
                }
                navigate("/verify");
                Analytics.trackEngagement(Analytics.SIGN_UP);
            })
            .catch((error) => {
                const detail =
                    error?.response?.data?.detail ||
                    "There was an error signing up, please contact our support";
                if (Array.isArray(detail)) {
                    setErrors(detail.map((e) => e.msg));
                } else {
                    setErrors([detail]);
                }
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const googleButton = useRef(null);
    useEffect(() => {
        const script = document.createElement("script");
        script.src = "https://accounts.google.com/gsi/client";
        script.async = true;

        script.onload = () => {
            google.accounts.id.initialize({
                client_id: GOOGLE_CLIENT_ID,
                callback: (response) => {
                    signUp({
                        source: "google",
                        google_jwt: response.credential,
                    });
                },
            });

            const dimensions = googleButton.current.getBoundingClientRect();
            google.accounts.id.renderButton(googleButton.current, {
                theme: "outline",
                size: "large",
                text: "signup_with",
                logo_alignment: "center",
                width: Math.min(dimensions.width, 400),
            });
        };
        document.body.appendChild(script);
        return () => {
            document.body.removeChild(script);
        };
    }, []);

    useEffect(() => {
        if (localStorage.getItem("themeMode") != "dark") {
            toggleTheme();
        }
    }, []);

    const onSubmit = (event) => {
        event.preventDefault();

        const newErrors = [];
        if (password != password2) {
            newErrors.push("Passwords do not match.");
        }
        if (!agree) {
            newErrors.push(
                "Missing consent to Privacy Policy and Terms of Use.",
            );
        }
        if (newErrors.length > 0) {
            setErrors(newErrors);
            return;
        }

        signUp({
            source: "email",
            email: email,
            password: password,
            first_name: firstName,
            last_name: lastName,
            company_name: companyName,
            device_id: localStorage.getItem("deviceId"),
        });
    };

    const handleCloseError = () => {
        setErrors([]);
    };

    return (
        <AuthContainer isMobile={isMobile}>
            <Helmet>
                <PageTitle title="Sign Up" />
            </Helmet>
            <GlobalStyle theme={themeMode} />
            <AuthNav internalLinkLabel="Sign In" internalLinkUrl="/login" />

            {step == 1 && (
                <AuthVideo theme={theme} isMobile={isMobile}>
                    <div>
                        <video
                            autoPlay
                            loop
                            disablePictureInPicture
                            playsInline
                            src="/makeos-feature-preview.mp4"
                        />
                        <aside>{/* Gradient overlay */}</aside>
                    </div>
                    <Heading>
                        Your new AI
                        <br />
                        production workspace
                    </Heading>
                    <SubHeading>
                        Turn messy client requests into briefs, budgets, and
                        timelines - assisted by AI - and go from zero to
                        production in minutes.
                    </SubHeading>
                </AuthVideo>
            )}

            <AuthSection>
                {step == 1 && (
                    <form onSubmit={() => setStep(2)}>
                        <Grid container direction="column" spacing={4}>
                            <Grid item>
                                <Heading>Sign Up</Heading>
                            </Grid>
                            <Grid item>
                                <SubHeading>
                                    {invitation?.inviter
                                        ? `${invitation.inviter.name} has invited you to join their workspace`
                                        : "Welcome, choose your method to get started"}
                                </SubHeading>
                            </Grid>
                            <Grid item>
                                <GoogleLoginButton ref={googleButton} />
                            </Grid>
                            <Grid
                                item
                                container
                                alignItems="center"
                                spacing={1}
                            >
                                <Grid item flexGrow={1}>
                                    <OrLine
                                        backgroundColor={
                                            theme.palette.text.primary
                                        }
                                    />
                                </Grid>
                                <Grid item>OR</Grid>
                                <Grid item flexGrow={1}>
                                    <OrLine
                                        backgroundColor={
                                            theme.palette.text.primary
                                        }
                                    />
                                </Grid>
                            </Grid>
                            <Grid item textAlign="right">
                                <TextField
                                    disabled={invitation?.invitee.email}
                                    label="Email"
                                    type="email"
                                    variant="filled"
                                    fullWidth
                                    sx={{ marginBottom: "16px" }}
                                    InputProps={{
                                        disableUnderline: true,
                                    }}
                                    InputLabelProps={{
                                        required: false,
                                    }}
                                    required
                                    value={email}
                                    onChange={(event) =>
                                        setEmail(event.target.value)
                                    }
                                    data-testid="signup-email-input"
                                />

                                {invitation ? null : (
                                    <InternalLink href="/login">
                                        Already have an account?
                                    </InternalLink>
                                )}
                            </Grid>

                            <Grid item>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    size="large"
                                    color="primary"
                                    sx={{ color: "white" }}
                                    endIcon={<LoginIcon />}
                                    fullWidth
                                    data-testid="signup-step-1-submit-button"
                                >
                                    Sign up
                                </Button>
                            </Grid>
                        </Grid>
                    </form>
                )}

                {step == 2 && (
                    <form onSubmit={onSubmit}>
                        <Grid container direction="column" spacing={5}>
                            <Grid item container direction="column" spacing={2}>
                                <Grid item>
                                    <Heading>Create your account</Heading>
                                </Grid>
                                <Grid item>
                                    <SubHeading>
                                        Set a password to get started
                                    </SubHeading>
                                </Grid>
                            </Grid>

                            <Grid item container direction="column" spacing={2}>
                                <Grid item>
                                    <TextField
                                        label="First name"
                                        variant="filled"
                                        fullWidth
                                        InputProps={{
                                            disableUnderline: true,
                                        }}
                                        value={firstName}
                                        onChange={(event) =>
                                            setFirstName(event.target.value)
                                        }
                                        required
                                        data-testid="signup-firstname-input"
                                    />
                                </Grid>
                                <Grid item>
                                    <TextField
                                        label="Last name"
                                        variant="filled"
                                        fullWidth
                                        InputProps={{
                                            disableUnderline: true,
                                        }}
                                        value={lastName}
                                        onChange={(event) =>
                                            setLastName(event.target.value)
                                        }
                                        required
                                        data-testid="signup-lastname-input"
                                    />
                                </Grid>
                                <Grid item>
                                    <TextField
                                        label="Company"
                                        variant="filled"
                                        fullWidth
                                        InputProps={{
                                            disableUnderline: true,
                                        }}
                                        value={companyName}
                                        onChange={(event) =>
                                            setCompanyName(event.target.value)
                                        }
                                        data-testid="signup-company-input"
                                    />
                                </Grid>
                                <Grid item>
                                    <TextField
                                        label="Password"
                                        type="password"
                                        variant="filled"
                                        fullWidth
                                        InputProps={{
                                            disableUnderline: true,
                                        }}
                                        value={password}
                                        onChange={(event) =>
                                            setPassword(event.target.value)
                                        }
                                        required
                                        data-testid="signup-password-input"
                                    />
                                </Grid>
                                <Grid item>
                                    <TextField
                                        label="Confirm password"
                                        type="password"
                                        variant="filled"
                                        fullWidth
                                        InputProps={{
                                            disableUnderline: true,
                                        }}
                                        value={password2}
                                        onChange={(event) =>
                                            setPassword2(event.target.value)
                                        }
                                        required
                                        data-testid="signup-confirm-password-input"
                                    />
                                </Grid>
                                <Grid item>
                                    <RadioGroup
                                        value={agree}
                                        onClick={() => setAgree(!agree)}
                                    >
                                        <FormControlLabel
                                            value={true}
                                            control={<Radio />}
                                            label="By signing up, you agree to our Privacy Policy and Terms of Use."
                                            data-testid="signup-terms-radio"
                                        />
                                    </RadioGroup>
                                </Grid>
                            </Grid>

                            <Grid item>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    size="large"
                                    color="primary"
                                    sx={{ color: "white" }}
                                    fullWidth
                                    disabled={loading}
                                    endIcon={<LoginIcon />}
                                    data-testid="signup-step-2-submit-button"
                                >
                                    Sign up
                                </Button>
                            </Grid>
                        </Grid>

                        <Footer>
                            <Link color="inherit" onClick={() => setStep(1)}>
                                Back
                            </Link>
                        </Footer>
                    </form>
                )}
            </AuthSection>

            <Snackbar
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                open={errors.length > 0}
                onClose={handleCloseError}
            >
                <Alert
                    variant="filled"
                    severity="error"
                    sx={{ width: "100%" }}
                    onClose={handleCloseError}
                >
                    {errors.join(", ")}
                </Alert>
            </Snackbar>
        </AuthContainer>
    );
}
